import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

/**
 * BlueRock AntD ConfigProvider theme. Apps wire it as:
 *   <ConfigProvider theme={brTheme(isDark)}>...</ConfigProvider>
 *
 * Both modes override AntD's defaults to match agate's palette (defined in
 * `colors.css`). AntD's CSS-in-JS doesn't read CSS vars, so we have to repeat
 * the values here as JS strings — keep both sides in sync when changing either
 * ramp.
 *
 * Dark:
 *   --color-bg              #1B1B1B   ← page / layout background (agate MainLayout)
 *   --color-surface         #141414   ← cards / sidebar (agate Sidebar + widgets)
 *   --color-surface-raised  #1E1E1E   ← inputs / dropdowns (agate chart panels)
 *
 * Light:
 *   --color-bg              #fafcfe   ← page / layout background (agate MainLayout)
 */
const DARK = {
  bg: '#1B1B1B',
  surface: '#141414',
  raised: '#1E1E1E',
  border: 'rgba(255, 255, 255, 0.08)',
  borderSubtle: 'rgba(255, 255, 255, 0.04)',
  textBase: '#f5f5f5',
};

/** `hex` as an rgba() string at the given alpha (0-1) — for text tiers that
 *  need a specific alpha AntD's own default percentage doesn't provide. */
function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Light-mode values that must track `colors.css`. Only the ones we actually
 *  override live here — light mode otherwise rides AntD's own defaults.
 *
 *  `border`/`textBase` are garnet-console's warm-ink palette (Colin
 *  Harker's onboarding work, 2026-06-23), promoted to the actual corenet
 *  default — see the matching comment in colors.css for the full context
 *  and why this is a deliberate global decision, not a garnet-console-only
 *  one. `border` matches `--color-border` exactly (also matches
 *  garnet-console's own prior Card.colorBorderSecondary override, which
 *  used the identical value — one border color, not a two-tier system).
 *  `textBase` matches `--color-text-heading`/`--chrome-text-strong`
 *  exactly (the 100%-alpha tier of the same ink) — AntD's own default 88%/
 *  65% alpha derivation from this seed already lands close to
 *  garnet-console's hand-tuned `--color-text` (85%) and `--chrome-text`
 *  (70%) without needing explicit overrides for those two tiers. */
const LIGHT = {
  bg: '#fafcfe',
  border: '#dde2e6',
  textBase: '#13181e',
};

export const brTheme = (isDark: boolean): ThemeConfig => ({
  // Expose AntD tokens as CSS variables (var(--ant-color-text), etc.) so
  // CSS files in corenet (and consumer apps) can reference theme-aware
  // colors without needing JS. `hashed: false` keeps the variable names
  // stable across instances, recommended pairing per AntD docs.
  cssVar: { key: 'br' },
  hashed: false,
  token: {
    colorPrimary: '#006bff',
    // AntD's `colorLink` seed defaults to `colorInfo`, which we never set
    // either — so any plain `<a>` (AntD's own global `a:hover` CSS reset
    // applies `colorLinkHover` to every one, unconditionally) rendered in
    // an arbitrary shade of AntD's stock info-blue, never actually chosen
    // by anyone. Brand blue itself (#006bff / dark-adjusted #035edc) reads
    // fine as a large button fill but is too close to WCAG's 4.5:1 text
    // floor to use verbatim as small link text — light mode fails by a
    // hair against the page bg (4.49:1), dark mode fails badly against
    // dark surfaces (~3:1).
    //
    // Light mode: `colorLink` is a static override — `token.colorLink`
    // renders exactly as given. Rather than nudge brand blue by a unit to
    // scrape past 4.5:1 (a second, near-identical brand constant), links
    // use AntD's own darker step of the brand palette —
    // `generate('#006bff')[6]`, i.e. what AntD itself derives as
    // `colorLinkActive` from the brand seed — which clears 4.5:1 with real
    // margin (6.30:1 on the page bg).
    //
    // Dark mode: `colorLink` is one of AntD's actual Seed Tokens (like
    // `colorPrimary`), NOT a static value — `@ant-design/colors`'
    // `generate(seed, { theme: 'dark' })` regenerates a full 10-step
    // palette from it, and the *rendered* `colorLink` is
    // `generate(...)`step 6, not the seed itself (same mechanism that
    // turns colorPrimary's `#006bff` seed into the rendered `#035edc`).
    // Feeding a literal target color in directly (e.g. the light-mode
    // approach) doesn't work — verified by trying `#247ffc`, which
    // rendered as `#226fd9` (3.56:1, still failing). `#3f99ff` below is a
    // seed chosen empirically — by running the actual
    // `@ant-design/colors` generate() and checking its real step-6
    // output — to land the *rendered* color at 4.56:1/4.88:1.
    colorLink: isDark ? '#3f99ff' : '#0053d9',
    // AntD derives `colorLinkHover` from `colorLink` via a fixed palette
    // index (position 4 of 10) that, unlike `colorPrimaryHover`'s index,
    // ISN'T remapped brighter for dark theme — `generate(color, {theme:
    // 'dark'})` still lands index 4 on a genuinely dark step, so even with
    // the correct brand-blue seed above, a hovered link's text gets darker
    // against an already-dark background: illegible, not a rendering bug.
    // Dark mode reuses `--color-brand-hover` from colors.css — the
    // existing, already-canonical "brand blue, hover variant" — which
    // independently clears 4.5:1 (6.16:1/5.76:1). Light mode's
    // `--color-brand-hover` (#1a7cff) does NOT clear 4.5:1 (3.91:1), and
    // AntD's own derivation from the darker link seed lands lighter still
    // (2.88:1) — so hover brightens to brand blue itself (#006bff, the same
    // constant as colorPrimary; 4.49:1 on a transient state), with no
    // nudged near-duplicate.
    colorLinkHover: isDark ? '#4096ff' : '#006bff',
    // Must track `--font-sans` in colors.css — AntD's CSS-in-JS doesn't
    // read CSS vars, so the value is repeated here as a JS string.
    fontFamily:
      "'DM Sans Variable', 'DM Sans', ui-sans-serif, system-ui, -apple-system, sans-serif",
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,

    // Neutral near-black surfaces for dark mode (override AntD's blue-tinted defaults).
    // Dark mode overrides `colorTextDescription` — AntD's Typography secondary
    // (`<Text type="secondary">`) reads THIS token (not colorTextSecondary) —
    // matching `colorTextSecondary` (#a3a3a3) for visual symmetry across
    // muted-text contexts.
    ...(isDark
      ? {
          colorBgBase: DARK.bg,
          colorBgLayout: DARK.bg,
          colorBgContainer: DARK.surface,
          colorBgElevated: DARK.surface,   // popovers/dropdowns/modals match chrome
          colorBgSpotlight: DARK.raised,
          colorBorder: DARK.border,
          colorBorderSecondary: DARK.borderSubtle,
          colorTextBase: DARK.textBase,
          colorText: '#d1d1d1',
          colorTextSecondary: '#a3a3a3',
          colorTextTertiary: '#8a8a8a',
          // #6b6b6b (AntD's stock dark colorTextQuaternary) is 3.46:1/3.23:1
          // against surface/page bg — fails 4.5:1 (not as badly as light
          // mode's equivalent, but still fails). #838383 is the minimum
          // grey that clears 4.5:1 against the harder case (page bg,
          // #1B1B1B): 4.54:1/4.86:1. colorTextPlaceholder isn't set
          // elsewhere in dark mode, so it was aliasing this same value
          // (antd/es/theme/util/alias.js) — set explicitly here so that's
          // not implicit.
          colorTextQuaternary: '#838383',
          colorTextPlaceholder: '#838383',
          colorTextDescription: '#a3a3a3',
          colorFill: 'rgba(255, 255, 255, 0.08)',
          colorFillSecondary: 'rgba(255, 255, 255, 0.06)',
          colorFillTertiary: 'rgba(255, 255, 255, 0.04)',
          colorFillQuaternary: 'rgba(255, 255, 255, 0.02)',
        }
      : {
          // Mirrors `--color-bg` from colors.css. AntD's default light
          // colorBgLayout (#f5f5f5) is a different shade from the page
          // background agate paints via `--color-bg`, so any page whose
          // content was shorter than the viewport showed a visible seam where
          // one stopped and the other showed through. Dark mode never had this
          // because `colorBgLayout: DARK.bg` below already aligns them.
          colorBgLayout: LIGHT.bg,
          colorBorder: LIGHT.border,
          colorBorderSecondary: LIGHT.border,
          colorTextBase: LIGHT.textBase,
          // AntD's stock alpha for these (45%/25%) fails WCAG AA against
          // the page bg regardless of which colorTextBase they're
          // composited from — verified against the warm ink specifically
          // (not just carried over from the old pure-black numbers):
          // minimum alpha for 4.5:1 is ~59.1% here (vs ~56.7% for pure
          // black — the warm ink has slightly higher luminance, so it
          // needs slightly more opacity to hit the same contrast). 62%/60%
          // clear it with margin while keeping separation both from each
          // other and from colorTextSecondary's existing 65% (AntD
          // default, left alone) — the compliant band is even narrower
          // than it was for pure black, so these three tiers necessarily
          // read close together. colorTextDisabled isn't set here — it
          // aliases colorTextQuaternary by default (antd/es/theme/util/
          // alias.js) and is WCAG-exempt regardless (1.4.3 excludes
          // disabled controls), so it just inherits this fix as a side
          // effect.
          colorTextTertiary: withAlpha(LIGHT.textBase, 0.62),
          colorTextDescription: withAlpha(LIGHT.textBase, 0.62),
          colorTextQuaternary: withAlpha(LIGHT.textBase, 0.6),
          colorTextPlaceholder: withAlpha(LIGHT.textBase, 0.6),
        }),
  },
  algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
  components: {
    Card: isDark
      ? {
          colorBgContainer: DARK.surface,
          colorBorderSecondary: DARK.border,
          headerBg: DARK.surface,
        }
      : {},
    Alert: isDark
      ? {
          colorErrorBg: 'rgba(239, 68, 68, 0.08)',
          colorErrorBorder: 'rgba(239, 68, 68, 0.18)',
        }
      : {},
    Tag: {
      borderRadius: 4,
      fontSize: 12,
      lineHeight: 20,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      colorError: '#ff4d4f',
      colorErrorBg: isDark ? '#2a1215' : '#fff2f0',
      colorErrorBorder: isDark ? '#58191c' : '#ffccc7',
    },
    Tabs: {
      itemColor: isDark ? '#8a8a8a' : 'rgba(0,0,0,0.88)',
      itemSelectedColor: '#006bff',
      itemHoverColor: isDark ? '#d1d1d1' : '#4b5563',
      inkBarColor: '#006bff',
    },
    // Sidebar/menu text used to be forced via `--chrome-text`/`-strong` CSS
    // `!important` overrides in app-shell.css — a second, hand-synced copy
    // of what these tokens already say. Menu items are real AntD components
    // inside the `.br` scope, so they don't need that detour: `itemColor`
    // and `groupTitleColor` are left unset (AntD's own defaults, `colorText`
    // and `colorTextDescription`, already read the right light/dark value).
    // `itemHoverColor`/`itemSelectedColor` ARE overridden — AntD's own
    // default for `itemSelectedColor` is `colorPrimary` (brand blue), which
    // is a real behavior change from corenet's existing near-black/bold
    // selected-item design, not just a mechanism swap.
    Menu: {
      itemHoverColor: isDark ? DARK.textBase : LIGHT.textBase,
      itemSelectedColor: isDark ? DARK.textBase : LIGHT.textBase,
    },
    Layout: isDark
      ? {
          headerBg: DARK.surface,
          siderBg: DARK.surface,
          bodyBg: DARK.bg,
        }
      : {},
    Modal: isDark
      ? {
          contentBg: DARK.surface,
          headerBg: DARK.surface,
        }
      : {},
    Drawer: isDark
      ? {
          colorBgElevated: DARK.surface,
        }
      : {},
    Popover: isDark
      ? {
          colorBgElevated: DARK.surface,
        }
      : {},
    Dropdown: isDark
      ? {
          colorBgElevated: DARK.surface,
        }
      : {},
    Select: isDark
      ? {
          colorBgContainer: DARK.raised,
          colorBgElevated: DARK.surface,
          optionSelectedBg: 'rgba(0, 107, 255, 0.15)',
        }
      : {},
    Input: isDark
      ? {
          colorBgContainer: DARK.raised,
        }
      : {},
    Button: isDark
      ? {
          colorBgContainer: DARK.raised,
          defaultBg: DARK.raised,
          defaultBorderColor: DARK.border,
        }
      : {},
    Steps: isDark
      ? {
          colorBgContainer: DARK.surface,
        }
      : {},
  },
});

/**
 * Layers local overrides on top of corenet's base theme for one app or
 * route. Use this instead of hand-editing antd.theme.ts/colors.css when
 * only one consumer needs to diverge — see corenet/CLAUDE.md "How to
 * give a feature or app its own theme".
 *
 *   <ConfigProvider theme={mergeTheme(isDark, {
 *     token: { colorPrimary: '#1559c4' },
 *   })}>
 *
 * Automatically picks up any future change to brTheme()'s defaults.
 * `token` overrides merge with the base token set. `components`
 * overrides deep-merge per component name — passing
 * `components: { Tabs: { itemSelectedColor: '#123' } }` replaces only
 * `Tabs.itemSelectedColor`; other `Tabs` fields (`itemColor`,
 * `itemHoverColor`, `inkBarColor`) still come from `brTheme()`. This
 * matches AntD's own `ConfigProvider` nested-theme merge behavior.
 */
export function mergeTheme(isDark: boolean, overrides: Partial<ThemeConfig> = {}): ThemeConfig {
  const base = brTheme(isDark);
  // Deep-merge per component name (not a strict per-key TS shape — AntD's
  // component token types don't intersect cleanly across component names,
  // so we merge as plain objects and cast back to ThemeConfig's shape).
  const mergedComponents: Record<string, object> = {
    ...(base.components as Record<string, object> | undefined),
  };
  for (const [name, config] of Object.entries(overrides.components ?? {})) {
    mergedComponents[name] = { ...(mergedComponents[name] ?? {}), ...(config as object) };
  }
  return {
    ...base,
    ...overrides,
    token: { ...base.token, ...overrides.token },
    components: mergedComponents as ThemeConfig['components'],
  };
}
